import { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useContextMenu } from "../../../contexts/RightClickMenuContext";
import { usePopup } from "../../../contexts/PopupContext";
import ShareModal from "../ShareModal";
import SharedWithMeSection from "../SharedWithMeSection";
import { Trash2, Star, Globe, Pencil, Share2 } from "lucide-react";

import { usePortalTarget, feedViewIcon } from "./sidebarUtils";
import { useLayout } from "../../../contexts/LayoutContext";
import { useSearchRegistry } from "../../Search/SearchRegistry";

const FeedSidebarPortal = () => {
  const [shareTarget, setShareTarget] = useState(null);
  const portalTarget = usePortalTarget("feed-portal-slot");
  const { currentAppPage, leftOpen } = useLayout();
  const navigate = useNavigate();

  const [systemViews, setSystemViews] = useState([]);
  const [customViews, setCustomViews] = useState([]);
  const [favoritedSources, setFavoritedSources] = useState([]);
  const [viewOrder, setViewOrder] = useState([]); // ordered keys
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [activeView, setActiveView] = useState({
    kind: "system",
    key: "Global",
  });
  const { setIsPopUpOpen, setPopUpComponent } = usePopup();

  // Clear active state when another section becomes active
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.section && e.detail.section !== "feed") {
        setActiveView({ kind: null, key: null });
      }
    };
    window.addEventListener("sidebar:active-section-changed", handler);
    return () =>
      window.removeEventListener("sidebar:active-section-changed", handler);
  }, []);

  // Drag state
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const { getFeedViews } = await import("../../../../api/feed");
      const res = await getFeedViews();
      const d = res?.data || {};
      if (d.system_views) setSystemViews(d.system_views);
      if (d.custom_views) setCustomViews(d.custom_views);
      if (d.favorited_sources) setFavoritedSources(d.favorited_sources);
      if (d.view_order) setViewOrder(d.view_order);
    } catch (e) {
      console.error("Error loading feed views:", e);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const handler = () => loadData();
    window.addEventListener("sidebar:refresh-feed-views", handler);
    return () =>
      window.removeEventListener("sidebar:refresh-feed-views", handler);
  }, [loadData]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail) setActiveView(e.detail);
    };
    window.addEventListener("feed:active-view-changed", handler);
    return () =>
      window.removeEventListener("feed:active-view-changed", handler);
  }, []);

  /* ── Breadcrumb: emit active sub-item ── */
  const emitFeedBreadcrumb = useCallback(() => {
    if (currentAppPage !== "/feed") return;
    let label = null;
    if (activeView.kind === "system") label = activeView.key;
    else if (activeView.kind === "favorited_source") label = activeView.source;
    else if (activeView.kind === "custom") label = activeView.name;
    window.dispatchEvent(
      new CustomEvent("breadcrumb:update", {
        detail: {
          section: "/feed",
          subLabel: label,
          subIcon: null,
          hasAdd: true,
          addKind: "feed",
          addLabel: "Add feed view",
        },
      }),
    );
  }, [activeView, currentAppPage]);

  useEffect(() => {
    emitFeedBreadcrumb();
  }, [emitFeedBreadcrumb, leftOpen]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.section === "/feed") emitFeedBreadcrumb();
    };
    window.addEventListener("breadcrumb:request", handler);
    return () => window.removeEventListener("breadcrumb:request", handler);
  }, [emitFeedBreadcrumb]);

  /* ── Breadcrumb: listen for add action ── */
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.section === "/feed") {
        // Navigate to feed and trigger the create modal
        if (currentAppPage !== "/feed") navigate("/feed");
        setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent("sidebar:feed-open-create-modal"),
          );
        }, 50);
      }
    };
    window.addEventListener("breadcrumb:add", handler);
    return () => window.removeEventListener("breadcrumb:add", handler);
  }, [currentAppPage, navigate]);

  /* ── Build unified ordered list ── */
  // Each item: { orderId, kind, data }
  const orderedItems = useMemo(() => {
    // Build a map of all items keyed by their order ID
    const itemMap = new Map();

    systemViews.forEach((v) => {
      const id = `system:${v.key}`;
      itemMap.set(id, { orderId: id, kind: "system", data: v });
    });

    favoritedSources.forEach((f) => {
      const id = `fav:${f.document_id}`;
      itemMap.set(id, { orderId: id, kind: "favorited_source", data: f });
    });

    customViews.forEach((v) => {
      const id = `custom:${v.document_id}`;
      itemMap.set(id, { orderId: id, kind: "custom", data: v });
    });

    // If we have a saved order, use it
    if (viewOrder.length > 0) {
      const result = [];
      const used = new Set();
      viewOrder.forEach((id) => {
        if (itemMap.has(id)) {
          result.push(itemMap.get(id));
          used.add(id);
        }
      });
      // Append any items not in the saved order (newly created)
      itemMap.forEach((item, id) => {
        if (!used.has(id)) result.push(item);
      });
      return result;
    }

    // Default order: system → favorites → custom
    return [...itemMap.values()];
  }, [systemViews, customViews, favoritedSources, viewOrder]);

  /* ── Search registry ── */
  const { register, unregister } = useSearchRegistry();
  useEffect(() => {
    register(
      "/feed",
      orderedItems.map((item) => {
        if (item.kind === "system") {
          const v = item.data;
          return {
            id: item.orderId,
            label: v.label,
            keywords: ["feed", "view"],
            icon: feedViewIcon(v.icon),
            iconColor: v.color || "rgba(14,165,233,0.70)",
            event: {
              navigateTo: "/feed",
              name: "sidebar:feed-switch-view",
              detail: { kind: "system", view: v },
            },
          };
        }
        if (item.kind === "favorited_source") {
          const f = item.data;
          return {
            id: item.orderId,
            label: f.source_label,
            keywords: ["feed", "favorite", "source"],
            icon: Globe,
            iconColor: "rgba(14,165,233,0.85)",
            event: {
              navigateTo: "/feed",
              name: "sidebar:feed-switch-view",
              detail: {
                kind: "favorited_source",
                document_id: f.document_id,
                source: f.source_value,
              },
            },
          };
        }
        if (item.kind === "custom") {
          const v = item.data;
          return {
            id: item.orderId,
            label: v.name,
            keywords: ["feed", "view", "custom"],
            icon: feedViewIcon(v.icon),
            iconColor: v.color || "rgba(148,163,184,0.60)",
            event: {
              navigateTo: "/feed",
              name: "sidebar:feed-switch-view",
              detail: { kind: "custom", view: v },
            },
          };
        }
        return {
          id: item.orderId,
          label: "Unknown",
          keywords: [],
        };
      }),
    );
    return () => unregister("/feed");
  }, [orderedItems, register, unregister]);

  const goToFeed = () => {
    if (currentAppPage !== "/feed") {
      navigate("/feed");
      navigate("/feed");
    }
  };

  // Dispatch AND stash: if MainFeeds hasn't mounted its listener yet (we just
  // navigated to /feed), the event is lost — the stash lets the page replay
  // the request on mount instead of needing a second click.
  const requestViewSwitch = (detail) => {
    window.__pendingFeedViewSwitch = detail;
    window.dispatchEvent(
      new CustomEvent("sidebar:feed-switch-view", { detail }),
    );
  };

  const handleSystemViewClick = (view) => {
    goToFeed();
    window.dispatchEvent(
      new CustomEvent("sidebar:active-section-changed", {
        detail: { section: "feed" },
      }),
    );
    requestViewSwitch({ kind: "system", view });
  };

  const handleFavoriteClick = (fav) => {
    goToFeed();
    window.dispatchEvent(
      new CustomEvent("sidebar:active-section-changed", {
        detail: { section: "feed" },
      }),
    );
    requestViewSwitch({
            kind: "favorited_source",
            document_id: fav.document_id,
            source: fav.source_value,
          });
  };

  const handleUnfavorite = async (e, fav) => {
    e.stopPropagation();
    try {
      const { unfavoriteFeedSource } = await import("../../../api/feed");
      await unfavoriteFeedSource({ document_id: fav.document_id });
      loadData();
      window.dispatchEvent(new CustomEvent("sidebar:refresh-feed-views"));
    } catch (err) {
      console.error("Unfavorite failed:", err);
    }
  };

  const handleViewClick = (view) => {
    goToFeed();
    window.dispatchEvent(
      new CustomEvent("sidebar:active-section-changed", {
        detail: { section: "feed" },
      }),
    );
    requestViewSwitch({ kind: "custom", view });
  };

  const handleDeleteView = async (e, view) => {
    e.stopPropagation();
    try {
      const { deleteFeedView } = await import("../../../api/feed");
      await deleteFeedView({ document_id: view.document_id });
      loadData();
      window.dispatchEvent(new CustomEvent("sidebar:refresh-feed-views"));
    } catch (err) {
      console.error("Delete view failed:", err);
    }
  };

  const handleEditView = (e, view) => {
    e.stopPropagation();
    goToFeed();
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("sidebar:feed-open-edit-modal", { detail: { view } }),
      );
    }, 50);
  };

  /* ── Context menu registrations ── */
  const confirmDeleteView = async (documentId) => {
    try {
      const { deleteFeedView } = await import("../../../api/feed");
      await deleteFeedView({ document_id: documentId });
      loadData();
      window.dispatchEvent(new CustomEvent("sidebar:refresh-feed-views"));
    } catch (err) {
      console.error("Delete view failed:", err);
    } finally {
      setPendingDeleteId(null);
    }
  };

  useContextMenu("feed-custom-view", [
    {
      label: "Share",
      icon: Share2,
      action: (payload) => {
        setPopUpComponent(
          <ShareModal
            onClose={() => setIsPopUpOpen(false)}
            contentType="custom_feed_view_v2"
            documentId={payload.document_id}
            itemName={payload.name}
          />,
        );
        setIsPopUpOpen(true);
      },
    },
    {
      label: "Edit View",
      icon: Pencil,
      action: (payload) => {
        goToFeed();
        setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent("sidebar:feed-open-edit-modal", {
              detail: { view: payload },
            }),
          );
        }, 50);
      },
    },
    {
      label: "Delete View",
      icon: Trash2,
      variant: "danger",
      action: (payload) => {
        setPendingDeleteId(payload.document_id);
      },
    },
  ]);

  useContextMenu("feed-favorited-source", [
    {
      label: "Remove Favorite",
      icon: Star,
      variant: "danger",
      action: async (payload) => {
        try {
          const { unfavoriteFeedSource } = await import("../../../api/feed");
          await unfavoriteFeedSource({ document_id: payload.document_id });
          loadData();
          window.dispatchEvent(new CustomEvent("sidebar:refresh-feed-views"));
        } catch (err) {
          console.error("Unfavorite failed:", err);
        }
      },
    },
  ]);

  /* ── Drag and drop ── */
  const handleDragStart = (e, idx) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = "move";
    // Make drag image semi-transparent
    if (e.currentTarget) {
      e.dataTransfer.setDragImage(e.currentTarget, 0, 0);
    }
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (idx !== dragOverIdx) setDragOverIdx(idx);
  };

  const handleDragLeave = () => {
    setDragOverIdx(null);
  };

  const handleDrop = async (e, dropIdx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === dropIdx) {
      setDragIdx(null);
      setDragOverIdx(null);
      return;
    }

    // Reorder
    const items = [...orderedItems];
    const [moved] = items.splice(dragIdx, 1);
    items.splice(dropIdx, 0, moved);

    const newOrder = items.map((item) => item.orderId);
    setViewOrder(newOrder);
    setDragIdx(null);
    setDragOverIdx(null);

    // Save to backend
    try {
      const { saveFeedViewOrder } = await import("../../../../api/feed");
      await saveFeedViewOrder({ order: newOrder });
    } catch (err) {
      console.error("Save order failed:", err);
    }
  };

  const handleDragEnd = () => {
    setDragIdx(null);
    setDragOverIdx(null);
  };

  /* ── Row style helpers ── */
  const rowClasses = (isActive) =>
    [
      "w-full text-left rounded-md px-2 py-1.5 text-[12px] truncate transition-all duration-200 flex items-center gap-x-1.5 group/row",
      isActive
        ? "text-[#0EA5E9] bg-[#0EA5E9]/[0.08] font-medium"
        : "text-zinc-500 dark:text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800/40",
    ].join(" ");

  /* ── Render a single item row ── */
  const renderItem = (item, idx) => {
    const isDragging = dragIdx === idx;
    const isDragOver = dragOverIdx === idx;

    const dragProps = {
      draggable: true,
      onDragStart: (e) => handleDragStart(e, idx),
      onDragOver: (e) => handleDragOver(e, idx),
      onDragLeave: handleDragLeave,
      onDrop: (e) => handleDrop(e, idx),
      onDragEnd: handleDragEnd,
    };

    const dragStyle = {
      opacity: isDragging ? 0.35 : 1,
      borderTop:
        isDragOver && dragIdx !== null && dragIdx > idx
          ? "2px solid rgba(14,165,233,0.5)"
          : undefined,
      borderBottom:
        isDragOver && dragIdx !== null && dragIdx < idx
          ? "2px solid rgba(14,165,233,0.5)"
          : undefined,
      cursor: "grab",
    };

    if (item.kind === "system") {
      const view = item.data;
      const Icon = feedViewIcon(view.icon);
      const isActive =
        activeView.kind === "system" && activeView.key === view.key;
      return (
        <div key={item.orderId} style={dragStyle} {...dragProps}>
          <button
            onClick={() => handleSystemViewClick(view)}
            className={rowClasses(isActive)}
          >
            <Icon
              className="h-3 w-3 shrink-0"
              strokeWidth={2}
              style={{ color: view.color || "rgba(14,165,233,0.70)" }}
            />
            <span className="truncate">{view.label}</span>
          </button>
        </div>
      );
    }

    if (item.kind === "favorited_source") {
      const fav = item.data;
      const isActive =
        activeView.kind === "favorited_source" &&
        activeView.document_id === fav.document_id;
      return (
        <div
          key={item.orderId}
          style={dragStyle}
          {...dragProps}
          data-context-menu="feed-favorited-source"
          data-context-payload={JSON.stringify(fav)}
        >
          <button
            onClick={() => handleFavoriteClick(fav)}
            className={rowClasses(isActive)}
          >
            <Globe
              className="h-3 w-3 shrink-0"
              strokeWidth={2}
              style={{ color: "rgba(14,165,233,0.85)" }}
            />
            <span className="truncate flex-1">{fav.source_label}</span>
            <Star
              className="h-3 w-3 shrink-0 text-amber-400 opacity-60"
              strokeWidth={2}
              fill="currentColor"
            />
          </button>
        </div>
      );
    }

    if (item.kind === "custom") {
      const view = item.data;
      const isActive =
        activeView.kind === "custom" &&
        activeView.document_id === view.document_id;
      const ViewIcon = feedViewIcon(view.icon);
      const isPendingDelete = pendingDeleteId === view.document_id;
      return (
        <div
          key={item.orderId}
          style={dragStyle}
          {...dragProps}
          data-context-menu="feed-custom-view"
          data-context-payload={JSON.stringify(view)}
        >
          <button
            onClick={() => handleViewClick(view)}
            className={rowClasses(isActive)}
          >
            <ViewIcon
              className="h-3 w-3 shrink-0"
              strokeWidth={2}
              style={{ color: view.color || "rgba(148,163,184,0.60)" }}
            />
            <span className="truncate flex-1">{view.name}</span>
          </button>

          {/* ── Inline delete confirmation ── */}
          <div
            className={[
              "overflow-hidden transition-all duration-200 ease-in-out",
              isPendingDelete
                ? "max-h-[80px] opacity-100 mt-0.5 mb-0.5"
                : "max-h-0 opacity-0",
            ].join(" ")}
          >
            {isPendingDelete && (
              <div className="flex items-center gap-x-2 py-1 px-1">
                <span className="text-[11px] text-slate-500 dark:text-gray-400">
                  Delete?
                </span>
                <button
                  onClick={() => setPendingDeleteId(null)}
                  className="text-[11px] px-2 py-0.5 rounded border border-slate-300 dark:border-slate-600 text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => confirmDeleteView(view.document_id)}
                  className="text-[11px] px-2 py-0.5 rounded border border-red-300 dark:border-red-500/30 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                >
                  Confirm
                </button>
              </div>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  const content = (
    <div className="flex flex-col gap-y-px">
      {/* ── Ordered items (drag & drop) ── */}
      {orderedItems.map((item, idx) => renderItem(item, idx))}
      <SharedWithMeSection
        contentType="feeds"
        onSelect={(item) => handleViewClick(item)}
      />
    </div>
  );

  if (!portalTarget) return null;
  return createPortal(content, portalTarget);
};

export default FeedSidebarPortal;
